import { useMemo, useState } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { PERMISSIONS } from '@oxxean/types';
import { createFinding, deleteFinding, fetchFindings, updateFinding } from '../../lib/api/manual-data';
import { queryKeys } from '../../lib/query-client';
import { errorMessage } from '../../lib/errors';
import { formatDate, toDateInputValue } from '../../lib/format';
import { useAuth } from '../../hooks/useAuth';
import { useCompanyAreas } from '../../hooks/useCompanyAreas';
import { useFilters } from '../../hooks/useFilters';
import { useTableState } from '../../hooks/useTableState';
import { useToasts } from '../../hooks/useToasts';
import { FINDING_SOURCE_LABEL, type CatalogOption, type Finding } from '../../types/domain';
import { DataBoundary } from '../../components/DataBoundary';
import { DataTable, type Column } from '../../components/DataTable';
import { ExportMenu } from '../../components/ExportMenu';
import { MetricDisplay } from '../../components/MetricDisplay';
import { ConfirmDialog, Modal } from '../../components/Modal';
import { Badge, Button, Card, Notice, TableSkeleton } from '../../components/primitives';
import { SelectField, TextAreaField, TextField } from '../../components/form';
import { PageHeading } from '../layout/PageHeading';
import { CaseStatusBadge, CatalogOptions, RiskBadge, RiskOptions, StatusOptions } from './common';
import {
  FINDING_DEFAULTS,
  findingFormSchema,
  toFindingInput,
  type FindingFormValues,
} from './schemas';

/**
 * Hallazgos de gestión.
 *
 * La columna «Resultado» muestra el `MetricValue` que disparó el hallazgo con su
 * estado: si el indicador no tenía registros, el hallazgo lo dice en lugar de
 * exhibir un porcentaje inventado.
 */
export function FindingsPage(): JSX.Element {
  const { can } = useAuth();
  const { filters, options } = useFilters();
  const { push } = useToasts();
  const queryClient = useQueryClient();
  const table = useTableState({ sortBy: 'detectedOn', sortDir: 'desc' });

  const [editing, setEditing] = useState<Finding | null>(null);
  const [formOpen, setFormOpen] = useState(false);
  const [pendingDelete, setPendingDelete] = useState<Finding | null>(null);

  const canManage = can(PERMISSIONS.FINDINGS_MANAGE);

  const listQuery = useQuery({
    queryKey: queryKeys.findings({ filters, state: table.state }),
    queryFn: () => fetchFindings(filters, table.state),
  });

  const invalidate = (): void => {
    void queryClient.invalidateQueries({ queryKey: ['findings'] });
  };

  const saveMutation = useMutation({
    mutationFn: async (values: FindingFormValues) => {
      const payload = toFindingInput(values);
      return editing === null ? createFinding(payload) : updateFinding(editing.id, payload);
    },
    onSuccess: () => {
      push({ kind: 'success', title: editing === null ? 'Hallazgo registrado' : 'Hallazgo actualizado' });
      setFormOpen(false);
      setEditing(null);
      invalidate();
    },
    onError: (error) => {
      push({ kind: 'error', title: 'No se pudo guardar', message: errorMessage(error) });
    },
  });

  const deleteMutation = useMutation({
    mutationFn: (id: string) => deleteFinding(id),
    onSuccess: () => {
      push({ kind: 'success', title: 'Hallazgo eliminado' });
      setPendingDelete(null);
      invalidate();
    },
    onError: (error) => {
      push({ kind: 'error', title: 'No se pudo eliminar', message: errorMessage(error) });
    },
  });

  const columns = useMemo<Array<Column<Finding>>>(
    () => [
      {
        key: 'detectedOn',
        header: 'Detectado',
        sortable: true,
        render: (row) => formatDate(row.detectedOn),
        plain: (row) => row.detectedOn,
      },
      {
        key: 'periodLabel',
        header: 'Período',
        sortable: true,
        render: (row) => row.periodLabel,
        plain: (row) => row.periodLabel,
      },
      {
        key: 'companyLabel',
        header: 'Empresa',
        sortable: true,
        render: (row) => row.companyLabel,
        plain: (row) => row.companyLabel,
      },
      {
        key: 'areaLabel',
        header: 'Área',
        sortable: true,
        render: (row) => row.areaLabel,
        plain: (row) => row.areaLabel,
      },
      {
        key: 'indicatorLabel',
        header: 'Indicador crítico',
        sortable: true,
        render: (row) => row.indicatorLabel,
        plain: (row) => row.indicatorLabel,
      },
      {
        key: 'result',
        header: 'Resultado',
        align: 'right',
        render: (row) => <MetricDisplay metric={row.result} format="percent" size="sm" inline hideNote />,
        plain: (row) => row.result.value,
      },
      {
        key: 'source',
        header: 'Origen',
        sortable: true,
        render: (row) => (
          <Badge tone={row.source === 'AUTOMATIC' ? 'blue' : 'neutral'}>
            {FINDING_SOURCE_LABEL[row.source]}
          </Badge>
        ),
        plain: (row) => row.source,
      },
      {
        key: 'risk',
        header: 'Riesgo',
        sortable: true,
        render: (row) => <RiskBadge risk={row.risk} />,
        plain: (row) => row.risk,
      },
      {
        key: 'action',
        header: 'Acción propuesta',
        render: (row) => (
          <span title={row.action}>
            {row.action.length > 80 ? `${row.action.slice(0, 80)}…` : row.action}
          </span>
        ),
        plain: (row) => row.action,
      },
      {
        key: 'responsible',
        header: 'Responsable',
        sortable: true,
        render: (row) => row.responsible,
        plain: (row) => row.responsible,
      },
      {
        key: 'dueDate',
        header: 'Fecha comprometida',
        sortable: true,
        render: (row) => formatDate(row.dueDate),
        plain: (row) => row.dueDate,
      },
      {
        key: 'status',
        header: 'Estado',
        sortable: true,
        render: (row) => <CaseStatusBadge status={row.status} />,
        plain: (row) => row.status,
      },
    ],
    [],
  );

  const rows = listQuery.data;

  return (
    <>
      <PageHeading
        title="Hallazgos"
        description="Hallazgos de gestión, su riesgo y las acciones comprometidas. Los de origen automático provienen de las reglas del motor de indicadores; los manuales se registran aquí."
        actions={
          <>
            <ExportMenu moduleKey="findings" />
            {canManage && (
              <Button
                variant="primary"
                icon="plus"
                onClick={() => {
                  setEditing(null);
                  setFormOpen(true);
                }}
              >
                Nuevo hallazgo
              </Button>
            )}
          </>
        }
      />

      <Notice tone="info">
        <strong>Sin registros no es cumplimiento.</strong> Un segmento sin documentos emitidos no genera
        un hallazgo de incumplimiento, pero tampoco se evalúa como riesgo bajo: queda sin evaluar hasta
        que existan registros.
      </Notice>

      <Card title="Hallazgos del período" flush>
        <DataBoundary
          isLoading={listQuery.isLoading}
          isError={listQuery.isError}
          error={listQuery.error}
          onRetry={() => void listQuery.refetch()}
          isEmpty={rows !== undefined && rows.total === 0 && table.state.search.length === 0}
          empty={{
            title: 'Sin hallazgos',
            message:
              'Ninguna regla se activó con los filtros aplicados y no hay hallazgos manuales registrados.',
          }}
          skeleton={<TableSkeleton columns={9} />}
        >
          {rows !== undefined && (
            <DataTable
              caption="Hallazgos de gestión"
              columns={columns}
              rows={rows.data}
              getRowId={(row) => row.id}
              state={table.state}
              onStateChange={table.setState}
              total={rows.total}
              totalPages={rows.totalPages}
              searchPlaceholder="Buscar por indicador, responsable o acción…"
              isFetching={listQuery.isFetching}
              rowActions={
                canManage
                  ? (row) => (
                      <>
                        <Button
                          size="sm"
                          variant="ghost"
                          icon="edit"
                          onClick={() => {
                            setEditing(row);
                            setFormOpen(true);
                          }}
                        >
                          Editar
                        </Button>
                        {row.source === 'MANUAL' && (
                          <Button
                            size="sm"
                            variant="ghost"
                            icon="trash"
                            onClick={() => setPendingDelete(row)}
                            aria-label="Eliminar hallazgo"
                          />
                        )}
                      </>
                    )
                  : undefined
              }
            />
          )}
        </DataBoundary>
      </Card>

      {formOpen && (
        <FindingForm
          editing={editing}
          companies={options?.companies}
          areasByCompany={options?.areasByCompany}
          saving={saveMutation.isPending}
          onCancel={() => {
            setFormOpen(false);
            setEditing(null);
          }}
          onSubmit={(values) => saveMutation.mutate(values)}
        />
      )}

      <ConfirmDialog
        open={pendingDelete !== null}
        title="Eliminar hallazgo"
        destructive
        confirmLabel="Eliminar"
        loading={deleteMutation.isPending}
        message="Sólo se pueden eliminar hallazgos registrados manualmente. La acción queda en la auditoría."
        onCancel={() => setPendingDelete(null)}
        onConfirm={() => {
          if (pendingDelete !== null) deleteMutation.mutate(pendingDelete.id);
        }}
      />
    </>
  );
}

interface FormProps {
  editing: Finding | null;
  companies: CatalogOption[] | undefined;
  /** Todas las áreas con su `companyId`: el formulario encadena por empresa. */
  areasByCompany: CatalogOption[] | undefined;
  saving: boolean;
  onCancel: () => void;
  onSubmit: (values: FindingFormValues) => void;
}

function FindingForm({
  editing,
  companies,
  areasByCompany,
  saving,
  onCancel,
  onSubmit,
}: FormProps): JSX.Element {
  const defaults: FindingFormValues =
    editing === null
      ? FINDING_DEFAULTS
      : {
          companyId: editing.companyId ?? '',
          areaId: editing.areaId,
          indicatorKey: editing.indicatorKey,
          indicatorLabel: editing.indicatorLabel,
          risk: editing.risk,
          action: editing.action,
          responsible: editing.responsible,
          dueDate: toDateInputValue(editing.dueDate),
          status: editing.status,
          comment: editing.comment ?? '',
          detectedOn: toDateInputValue(editing.detectedOn),
          closedOn: toDateInputValue(editing.closedOn),
        };

  const {
    register,
    handleSubmit,
    watch,
    setValue,
    formState: { errors },
  } = useForm<FindingFormValues>({
    resolver: zodResolver(findingFormSchema),
    defaultValues: defaults,
  });

  const automatic = editing?.source === 'AUTOMATIC';

  const { areas, disabled: areaDisabled, hint: areaHint } = useCompanyAreas({
    areasByCompany,
    companyId: watch('companyId'),
    areaId: watch('areaId'),
    // Un hallazgo automático trae el par empresa-área del motor y no se edita: si
    // el catálogo lo contradijera, el dato a revisar es el catálogo, no el hallazgo.
    onAreaCleared: () => {
      if (!automatic) setValue('areaId', '');
    },
  });

  return (
    <Modal
      open
      size="wide"
      title={editing === null ? 'Nuevo hallazgo' : 'Editar hallazgo'}
      onClose={onCancel}
      footer={
        <>
          <Button onClick={onCancel} disabled={saving}>
            Cancelar
          </Button>
          <Button variant="primary" loading={saving} onClick={() => void handleSubmit(onSubmit)()}>
            Guardar
          </Button>
        </>
      }
    >
      {automatic && (
        <Notice tone="info">
          Este hallazgo lo generó una regla del motor de indicadores. Su indicador y resultado no se
          editan: sólo la gestión (acción, responsable, plazo y estado).
        </Notice>
      )}

      <form className="form-grid" onSubmit={(event) => void handleSubmit(onSubmit)(event)} noValidate>
        <SelectField
          label="Empresa"
          disabled={automatic}
          error={errors.companyId?.message}
          {...register('companyId')}
        >
          <CatalogOptions options={companies} placeholder="Seleccione…" />
        </SelectField>

        <SelectField
          label="Área"
          disabled={automatic || areaDisabled}
          hint={automatic ? undefined : areaHint}
          error={errors.areaId?.message}
          {...register('areaId')}
        >
          <CatalogOptions
            options={areas}
            placeholder={areaDisabled ? 'Elija la empresa primero…' : 'Seleccione…'}
          />
        </SelectField>

        <TextField
          label="Clave del indicador"
          required
          disabled={automatic}
          hint="Identificador técnico, por ejemplo: documents.contracts."
          error={errors.indicatorKey?.message}
          {...register('indicatorKey')}
        />

        <TextField
          label="Indicador crítico"
          required
          disabled={automatic}
          error={errors.indicatorLabel?.message}
          {...register('indicatorLabel')}
        />

        <TextField
          label="Fecha de detección"
          type="date"
          required
          disabled={automatic}
          error={errors.detectedOn?.message}
          {...register('detectedOn')}
        />

        <SelectField label="Riesgo" required error={errors.risk?.message} {...register('risk')}>
          <RiskOptions />
        </SelectField>

        <TextAreaField
          label="Acción propuesta"
          required
          full
          error={errors.action?.message}
          {...register('action')}
        />

        <TextField
          label="Responsable"
          required
          error={errors.responsible?.message}
          {...register('responsible')}
        />

        <TextField
          label="Fecha comprometida"
          type="date"
          error={errors.dueDate?.message}
          {...register('dueDate')}
        />

        <SelectField label="Estado" required error={errors.status?.message} {...register('status')}>
          <StatusOptions />
        </SelectField>

        <TextField
          label="Fecha de cierre"
          type="date"
          hint="Obligatoria cuando el estado es «Cerrado»."
          error={errors.closedOn?.message}
          {...register('closedOn')}
        />

        <TextAreaField label="Comentario" full error={errors.comment?.message} {...register('comment')} />
      </form>
    </Modal>
  );
}
